import { Check, Circle, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  PASSWORD_BONUS_LABEL,
  evaluatePassword,
} from "@/lib/passwordPolicy";
import { usePasswordLeakCheck, LEAK_CHECK_LABEL } from "@/lib/leakedPassword";

interface PasswordStrengthIndicatorProps {
  password: string;
}

export function PasswordStrengthIndicator({
  password,
}: PasswordStrengthIndicatorProps) {
  // Empty field: still show every requirement (all unmet) so people know the
  // rules before they start typing.

  const { requirements, allRequiredMet, metCount, hasSymbol } = evaluatePassword(password);
  // Checked once every other rule is met, so a leaked password is caught here
  // rather than by an error after submitting. Every screen that sets a
  // password shows this list, so they all get the line.
  const leakState = usePasswordLeakCheck(password, allRequiredMet);
  const leaked = leakState === "leaked";

  // Progress reflects the mandatory rules only, plus the optional symbol as a
  // final step once everything required is satisfied.
  const total = requirements.length + 1;
  const progress = allRequiredMet && hasSymbol ? total : metCount;
  const percentage = (progress / total) * 100;

  const label = !allRequiredMet
    ? "Password requirements not met yet"
    : leaked
      ? "Choose a different password"
      : hasSymbol
      ? "Strong"
      : "Good";

  const barColor = leaked
    ? "bg-destructive"
    : !allRequiredMet
    ? metCount <= 1
      ? "bg-destructive"
      : "bg-honey"
    : "bg-sage";

  const labelColor = leaked
    ? "text-destructive"
    : !allRequiredMet
    ? metCount <= 1
      ? "text-destructive"
      : "text-honey"
    : "text-sage";

  return (
    <div className="space-y-2">
      {password && (
        <>
          <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
            <div
              className={cn("h-full rounded-full transition-all duration-300", barColor)}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <p className={cn("text-xs font-medium", labelColor)}>{label}</p>
        </>
      )}

      <ul className="space-y-1">
        {requirements.map((req) => (
          <li
            key={req.id}
            className={cn(
              "flex items-center gap-1.5 text-xs",
              req.met ? "text-sage" : "text-muted-foreground"
            )}
          >
            {req.met ? (
              <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
            ) : (
              <Circle className="h-3 w-3 shrink-0" aria-hidden="true" />
            )}
            <span>{req.label}</span>
          </li>
        ))}
        <li
          className={cn(
            "flex items-center gap-1.5 text-xs",
            leakState === "clear" ? "text-sage" : leaked ? "text-destructive" : "text-muted-foreground"
          )}
          data-testid="password-leak-check"
          data-state={leakState}
        >
          {leakState === "clear" ? (
            <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
          ) : leaked ? (
            <X className="h-3 w-3 shrink-0" aria-hidden="true" />
          ) : leakState === "checking" ? (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
          ) : (
            <Circle className="h-3 w-3 shrink-0" aria-hidden="true" />
          )}
          <span>{LEAK_CHECK_LABEL[leakState]}</span>
        </li>
      </ul>

      <ul className="space-y-1 border-t border-border pt-1.5">
        <li
          className={cn(
            "flex items-center gap-1.5 text-xs",
            hasSymbol ? "text-sage" : "text-muted-foreground"
          )}
        >
          {hasSymbol ? (
            <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
          ) : (
            <Circle className="h-3 w-3 shrink-0" aria-hidden="true" />
          )}
          <span>{PASSWORD_BONUS_LABEL}</span>
        </li>
      </ul>
    </div>
  );
}
